import React, { useState, useEffect, useRef } from "react";
import AddEvent from "../components/NewEvent";
import EditEvent from "../components/EditEvent";
import DeleteEvent from "../components/DeleteEvent";
import useUserData from "../hooks/useUserData";
import useEvents from "../hooks/useEvents";

const css = `
  *, *::before, *::after { margin: 0; padding: 0; box-sizing: border-box; }
  :root {
    --teal: #074F4F;
    --gold: #C6A769;
    --cream: #FAF7F2;
    --cream-d: #F0EBE3;
    --white: #fff;
    --text: #2d2d2d;
    --text-l: #8a8a8a;
    --text-ll: #b5b5b5;
    --serif: 'Cormorant', Georgia, serif;
    --sans: 'Inter', sans-serif;
    --ease: cubic-bezier(0.16, 1, 0.3, 1);
  }
  .evp-root {
    font-family: var(--sans);
    background: var(--cream);
    color: var(--text);
    overflow-x: hidden;
    -webkit-font-smoothing: antialiased;
  }
  .evp-root a { text-decoration: none; }
  a { color: inherit; }
  .evp-root img { display: block; max-width: 100%; }
  .evp-root ::selection { background: var(--cream-d); color: var(--teal); }

  /* PRELOADER */
  .evp-pre {
    position: fixed; inset: 0;
    background: var(--cream);
    z-index: 10000;
    display: flex; align-items: center; justify-content: center; flex-direction: column;
    transition: opacity 1s ease 0.2s, visibility 1s ease 0.2s;
  }
  .evp-pre.done { opacity: 0; visibility: hidden; pointer-events: none; }
  .evp-pre-word {
    font-family: var(--serif); font-size: 32px; font-weight: 300;
    letter-spacing: 18px; text-transform: uppercase; color: var(--teal);
    opacity: 0; animation: evpPreIn 1s 0.3s forwards; padding-left: 18px;
  }
  .evp-pre-line {
    width: 100%; max-width: 180px; height: 1px; background: var(--gold);
    margin-top: 24px; transform: scaleX(0); animation: evpLineIn 1.2s 0.6s forwards;
  }
  @keyframes evpPreIn { from { opacity:0; letter-spacing:36px } to { opacity:1; letter-spacing:18px } }
  @keyframes evpLineIn { to { transform: scaleX(1) } }

  /* GRAIN */
  .evp-grain {
    position: fixed; inset: 0; z-index: 9990;
    pointer-events: none; opacity: 0.02;
    background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");
    background-size: 128px;
  }

  /* NAV */
  .evp-nav {
    position: fixed; top: 0; left: 0; right: 0; z-index: 100;
    mix-blend-mode: normal;
    background: rgba(250,247,242,0.92);
    backdrop-filter: blur(30px); -webkit-backdrop-filter: blur(30px);
  }
  .evp-nav-inner {
    max-width: 1400px; margin: 0 auto; padding: 28px 48px;
    display: flex; align-items: center; justify-content: space-between;
  }
  .evp-nav-logo {
    font-family: var(--serif); font-size: 22px; font-weight: 400;
    letter-spacing: 5px; text-transform: uppercase; color: var(--teal);
  }
  .evp-nav-links { display: flex; gap: 44px; list-style: none; }
  .evp-nav-links a {
    font-size: 12px; font-weight: 500; letter-spacing: 2px; text-transform: uppercase;
    color: var(--text); transition: all 0.4s var(--ease); position: relative; padding-bottom: 4px;
  }
  .evp-nav-links a::after {
    content: ''; position: absolute; bottom: 0; left: 0; width: 100%; height: 1px;
    background: var(--teal); transform: scaleX(0); transform-origin: right;
    transition: transform 0.4s var(--ease);
  }
  .evp-nav-links a:hover::after { transform: scaleX(1); transform-origin: left; }
  .evp-nav-links a:hover { color: var(--teal); }
  .evp-nav-links a.active { color: var(--teal); }
  .evp-nav-links a.active::after { transform: scaleX(1); }
  .evp-nav-auth { display: flex; align-items: center; gap: 20px; }
  .evp-nav-login {
    font-size: 11px; font-weight: 500; letter-spacing: 2px; text-transform: uppercase;
    color: var(--text-l); transition: all 0.4s var(--ease); position: relative; padding-bottom: 4px;
  }
  .evp-nav-login:hover { color: var(--gold); }
  .evp-nav-register {
    font-size: 11px; font-weight: 500; letter-spacing: 2px; text-transform: uppercase;
    color: var(--cream); background: var(--teal); padding: 10px 24px;
    transition: all 0.4s var(--ease); position: relative; overflow: hidden;
  }
  .evp-nav-register::before {
    content: ''; position: absolute; inset: 0; background: var(--gold);
    transform: translateY(101%); transition: transform 0.4s var(--ease);
  }
  .evp-nav-register:hover::before { transform: translateY(0); }
  .evp-nav-register span { position: relative; z-index: 1; }
  .evp-nav-register:hover span { color: var(--teal); }
  .evp-nav-toggle {
    display: none; flex-direction: column; justify-content: center; align-items: center;
    gap: 5px; width: 40px; height: 40px; background: none; border: none; cursor: pointer;
    flex-shrink: 0; z-index: 210;
  }
  .evp-nav-toggle span { display: block; width: 24px; height: 1.5px; background: var(--teal); transition: all 0.35s var(--ease); }
  .evp-nav-toggle.open span:nth-child(1) { transform: translateY(6.5px) rotate(45deg); }
  .evp-nav-toggle.open span:nth-child(2) { opacity: 0; }
  .evp-nav-toggle.open span:nth-child(3) { transform: translateY(-6.5px) rotate(-45deg); }
  .evp-nav-mobile {
    display: none; flex-direction: column; background: var(--cream);
    max-height: 0; overflow: hidden; transition: max-height 0.5s var(--ease);
    box-shadow: 0 12px 30px rgba(7,79,79,0.1);
  }
  .evp-nav-mobile.open { max-height: 600px; }
  .evp-nav-mobile-links { list-style: none; display: flex; flex-direction: column; padding: 12px 24px; }
  .evp-nav-mobile-links li { border-bottom: 1px solid rgba(7,79,79,0.06); }
  .evp-nav-mobile-links li:last-child { border-bottom: none; }
  .evp-nav-mobile-links a {
    display: block; padding: 16px 4px; font-size: 14px; font-weight: 500;
    letter-spacing: 1px; text-transform: uppercase; color: var(--text);
  }
  .evp-nav-mobile-links a:hover { color: var(--teal); }
  .evp-nav-mobile-auth { display: flex; flex-direction: column; gap: 12px; padding: 16px 24px 28px; }
  .evp-nav-mobile-auth .evp-nav-login { text-align: center; padding: 10px; }
  .evp-nav-mobile-auth .evp-nav-register { text-align: center; }
  .evp-nav-mobile-scrim {
    position: fixed; inset: 0; z-index: 90; background: rgba(7,79,79,0.3);
    opacity: 0; visibility: hidden; transition: all 0.4s var(--ease);
  }
  .evp-nav-mobile-scrim.open { opacity: 1; visibility: visible; }
  .evp-nav-user-wrap { position: relative; }
  .evp-nav-user {
    display: flex; align-items: center; gap: 12px; font-size: 12px; font-weight: 500;
    letter-spacing: 1px; color: var(--teal); cursor: pointer; user-select: none;
  }
  .evp-nav-user-name { font-size: 12px; font-weight: 500; letter-spacing: 1.5px; text-transform: uppercase; }
  .evp-nav-user-avatar {
    width: 36px; height: 36px; border-radius: 50%; border: 1.5px solid var(--teal);
    display: flex; align-items: center; justify-content: center; font-family: var(--serif);
    font-size: 14px; color: var(--teal); background: rgba(7,79,79,0.05);
    transition: all 0.4s var(--ease);
  }
  .evp-nav-user:hover .evp-nav-user-avatar { background: var(--teal); color: var(--cream); }
  .evp-nav-user-chevron { width: 10px; height: 10px; opacity: 0.5; transition: all 0.3s var(--ease); }
  .evp-nav-user-wrap.open .evp-nav-user-chevron { transform: rotate(180deg); opacity: 0.8; }
  .evp-nav-dropdown {
    position: absolute; top: calc(100% + 16px); right: 0; min-width: 220px;
    background: var(--white); box-shadow: 0 4px 24px rgba(7,79,79,0.08), 0 1px 3px rgba(7,79,79,0.04);
    opacity: 0; visibility: hidden; transform: translateY(-8px);
    transition: all 0.35s var(--ease); z-index: 200; overflow: hidden;
  }
  .evp-nav-dropdown::before { content: ''; position: absolute; top: 0; left: 0; right: 0; height: 2px; background: var(--gold); }
  .evp-nav-user-wrap.open .evp-nav-dropdown { opacity: 1; visibility: visible; transform: translateY(0); }
  .evp-nav-dd-header { padding: 20px 24px 16px; border-bottom: 1px solid rgba(7,79,79,0.06); }
  .evp-nav-dd-name { font-family: var(--serif); font-size: 18px; color: var(--teal); margin-bottom: 2px; }
  .evp-nav-dd-email { font-size: 11px; color: var(--text-l); font-weight: 300; }
  .evp-nav-dd-items { padding: 8px 0; }
  .evp-nav-dd-item {
    display: flex; align-items: center; gap: 12px; padding: 12px 24px; font-size: 12px;
    letter-spacing: 0.5px; color: var(--text); cursor: pointer; transition: all 0.3s var(--ease);
    border: none; background: none; width: 100%; text-align: left;
  }
  .evp-nav-dd-item:hover { background: var(--cream); color: var(--teal); }
  .evp-nav-dd-item svg { width: 16px; height: 16px; opacity: 0.4; transition: opacity 0.3s; }
  .evp-nav-dd-item:hover svg { opacity: 0.7; }
  .evp-nav-dd-divider { height: 1px; background: rgba(7,79,79,0.06); }
  .evp-nav-dd-item.logout { color: #b33; }
  .evp-nav-dd-item.logout:hover { background: rgba(187,51,51,0.04); color: #b33; }

  /* HERO */
  .evp-hero {
    background: var(--cream); padding: 190px 48px 90px; text-align: center;
    position: relative; overflow: hidden;
  }
  .evp-hero::before {
    content: ''; position: absolute; top: -40%; left: 50%; transform: translateX(-50%);
    width: 90vw; height: 90vw; max-width: 1100px; max-height: 1100px; border-radius: 50%;
    background: radial-gradient(circle, rgba(198,167,105,0.09) 0%, transparent 62%);
    pointer-events: none;
  }
  .evp-label { font-size: 11px; font-weight: 500; letter-spacing: 3px; text-transform: uppercase; color: var(--gold); margin-bottom: 18px; position: relative; z-index: 1; }
  .evp-hero h1 {
    font-family: var(--serif); font-size: clamp(42px, 6vw, 82px); font-weight: 300;
    color: var(--teal); line-height: 1.08; margin-bottom: 22px; position: relative; z-index: 1;
  }
  .evp-hero h1 em { font-style: italic; color: var(--gold); }
  .evp-hero p {
    font-size: 15px; font-weight: 300; color: var(--text-l); max-width: 520px;
    margin: 0 auto; line-height: 1.9; position: relative; z-index: 1;
  }

  /* CONTACT STRIP */
  .evp-strip {
    max-width: 720px; margin: 0 auto; padding: 0 24px 90px;
    display: flex; justify-content: center; gap: 16px; flex-wrap: wrap; position: relative; z-index: 1;
  }
  .evp-strip a {
    display: flex; align-items: center; gap: 10px; padding: 16px 30px;
    font-size: 11px; font-weight: 500; letter-spacing: 1.5px; text-transform: uppercase;
    position: relative; overflow: hidden; transition: color 0.4s, box-shadow 0.4s;
  }
  .evp-strip a::before {
    content: ''; position: absolute; inset: 0; background: var(--gold);
    transform: translateY(101%); transition: transform 0.5s var(--ease);
  }
  .evp-strip a:hover { color: var(--teal); box-shadow: 0 10px 28px rgba(7,79,79,0.14); }
  .evp-strip a:hover::before { transform: translateY(0); }
  .evp-strip a span, .evp-strip a svg { position: relative; z-index: 1; }
  .evp-strip a.primary { background: var(--teal); color: var(--cream); border: 1px solid var(--teal); box-shadow: 0 6px 20px rgba(7,79,79,0.18); }
  .evp-strip a.secondary { background: var(--white); color: var(--teal); border: 1px solid rgba(7,79,79,0.16); }
  .evp-strip a.secondary::before { background: var(--teal); }
  .evp-strip a.secondary:hover { color: var(--cream); }

  /* SECTION HEAD */
  .evp-section { max-width: 1300px; margin: 0 auto; padding: 0 48px 100px; }
  .evp-sec-head {
    display: flex; align-items: flex-end; justify-content: space-between;
    margin-bottom: 44px; gap: 24px; flex-wrap: wrap;
  }
  .evp-sec-head h2 {
    font-family: var(--serif); font-size: clamp(28px, 3vw, 38px); font-weight: 300; color: var(--teal);
  }
  .evp-sec-head h2 em { font-style: italic; color: var(--gold); }
  .evp-add-btn {
    display: inline-flex; align-items: center; gap: 10px; padding: 13px 26px;
    background: var(--teal); color: var(--cream); font-size: 12px; font-weight: 500;
    letter-spacing: 1.5px; text-transform: uppercase; border: none; cursor: pointer;
    transition: all 0.35s var(--ease); flex-shrink: 0;
  }
  .evp-add-btn:hover { background: var(--gold); color: var(--teal); }
  .evp-add-btn svg { width: 14px; height: 14px; }

  /* GRID */
  .evp-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(320px, 1fr)); gap: 36px; }
  .evp-card {
    background: var(--white); position: relative; display: flex; flex-direction: column;
    box-shadow: 0 2px 24px rgba(7,79,79,0.06); transition: transform 0.4s var(--ease), box-shadow 0.4s var(--ease);
  }
  .evp-card:hover { transform: translateY(-6px); box-shadow: 0 18px 40px rgba(7,79,79,0.12); }
  .evp-card-media { position: relative; aspect-ratio: 4/3; overflow: hidden; background: var(--cream-d); }
  .evp-card-media img { width: 100%; height: 100%; object-fit: cover; transition: transform 0.6s var(--ease); }
  .evp-card:hover .evp-card-media img { transform: scale(1.05); }
  .evp-card-media::after {
    content: ''; position: absolute; inset: 0;
    background: linear-gradient(180deg, rgba(7,79,79,0) 55%, rgba(7,79,79,0.55) 100%);
  }
  .evp-new-badge {
    position: absolute; top: 16px; left: 0; z-index: 2;
    display: flex; align-items: center; gap: 6px;
    background: var(--gold); color: var(--teal); font-size: 10px; font-weight: 700;
    letter-spacing: 2px; text-transform: uppercase; padding: 7px 16px 7px 14px;
    box-shadow: 0 4px 14px rgba(0,0,0,0.15);
  }
  .evp-date-chip {
    position: absolute; top: 16px; right: 16px; z-index: 2;
    background: var(--cream); text-align: center; padding: 8px 12px; min-width: 52px;
    box-shadow: 0 4px 14px rgba(0,0,0,0.15);
  }
  .evp-date-chip .d { font-family: var(--serif); font-size: 22px; line-height: 1; color: var(--teal); font-weight: 500; }
  .evp-date-chip .m { font-size: 9px; letter-spacing: 1.5px; text-transform: uppercase; color: var(--gold); margin-top: 3px; }
  .evp-card-admin {
    position: absolute; bottom: 12px; right: 12px; z-index: 2; display: flex; gap: 8px;
  }
  .evp-icon-btn {
    width: 32px; height: 32px; border-radius: 50%; border: none; cursor: pointer;
    background: rgba(250,247,242,0.92); backdrop-filter: blur(6px);
    display: flex; align-items: center; justify-content: center; color: var(--teal);
    transition: all 0.3s var(--ease); font-size: 12px;
  }
  .evp-icon-btn:hover { background: var(--teal); color: var(--cream); }
  .evp-icon-btn.danger:hover { background: #b33; color: #fff; }
  .evp-card-body { padding: 26px 26px 28px; display: flex; flex-direction: column; gap: 12px; flex: 1; }
  .evp-card-title { font-family: var(--serif); font-size: 22px; font-weight: 400; color: var(--teal); line-height: 1.25; }
  .evp-card-meta { display: flex; align-items: center; gap: 8px; font-size: 11px; letter-spacing: 1px; text-transform: uppercase; color: var(--text-l); }
  .evp-card-meta svg { width: 13px; height: 13px; opacity: 0.6; flex-shrink: 0; }
  .evp-card-desc {
    font-size: 13px; line-height: 1.75; color: var(--text-l); font-weight: 300;
    display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden;
  }
  .evp-card-desc.open { -webkit-line-clamp: unset; }
  .evp-card-more {
    align-self: flex-start; font-size: 11px; font-weight: 500; letter-spacing: 1px;
    text-transform: uppercase; color: var(--teal); cursor: pointer; border-bottom: 1px solid var(--gold);
    padding-bottom: 2px; margin-top: auto; background: none; border-left: none; border-right: none; border-top: none;
  }
  .evp-card-more:hover { color: var(--gold); }

  /* EMPTY STATE */
  .evp-empty {
    text-align: center; padding: 70px 24px; border: 1px dashed rgba(7,79,79,0.18);
  }
  .evp-empty svg { width: 34px; height: 34px; color: var(--gold); margin-bottom: 18px; }
  .evp-empty p { font-family: var(--serif); font-size: 20px; color: var(--teal); font-style: italic; }
  .evp-empty span { display: block; margin-top: 8px; font-size: 13px; color: var(--text-l); font-weight: 300; }

  /* PAST TOGGLE */
  .evp-past-toggle {
    display: flex; align-items: center; gap: 12px; margin: 0 auto; background: none;
    border: none; cursor: pointer; color: var(--text-l); font-size: 12px; font-weight: 500;
    letter-spacing: 1.5px; text-transform: uppercase; padding: 14px 0;
  }
  .evp-past-toggle:hover { color: var(--teal); }
  .evp-past-toggle svg { width: 12px; height: 12px; transition: transform 0.3s var(--ease); }
  .evp-past-toggle.open svg { transform: rotate(180deg); }
  .evp-past-wrap { max-height: 0; overflow: hidden; transition: max-height 0.6s var(--ease); }
  .evp-past-wrap.open { max-height: 3000px; }
  .evp-past-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(260px, 1fr)); gap: 24px; padding-top: 36px; }
  .evp-past-card { display: flex; gap: 16px; opacity: 0.65; }
  .evp-past-media { width: 84px; height: 84px; flex-shrink: 0; overflow: hidden; background: var(--cream-d); }
  .evp-past-media img { width: 100%; height: 100%; object-fit: cover; filter: grayscale(0.5); }
  .evp-past-body h4 { font-family: var(--serif); font-size: 16px; font-weight: 400; color: var(--teal); margin-bottom: 4px; }
  .evp-past-body .evp-card-meta { margin-bottom: 0; }

  /* FOOTER */
  .evp-footer { background: var(--teal); padding: 80px 48px 32px; border-top: 1px solid rgba(198,167,105,0.25); }
  .evp-f-inner { max-width: 1200px; margin: 0 auto; }
  .evp-f-grid { display: grid; grid-template-columns: 2fr 1fr 1fr 1fr; gap: 48px; margin-bottom: 56px; }
  .evp-f-brand-name { font-family: var(--serif); font-size: 22px; font-weight: 400; letter-spacing: 5px; text-transform: uppercase; color: var(--cream); margin-bottom: 14px; }
  .evp-f-brand p { font-size: 13px; line-height: 1.7; color: rgba(250,247,242,0.4); max-width: 260px; }
  .evp-f-social { display: flex; gap: 22px; margin-top: 24px; }
  .evp-f-social a {
    width: 36px; height: 36px; border: 1px solid rgba(198,167,105,0.25); border-radius: 50%;
    display: flex; align-items: center; justify-content: center; font-size: 11px;
    letter-spacing: 1px; text-transform: uppercase; color: rgba(250,247,242,0.5); transition: all 0.3s;
  }
  .evp-f-social a:hover { background: var(--gold); border-color: var(--gold); color: var(--teal); }
  .evp-f-col h5 { font-size: 10px; font-weight: 500; letter-spacing: 2px; text-transform: uppercase; color: var(--gold); margin-bottom: 20px; }
  .evp-f-col ul { list-style: none; }
  .evp-f-col li { margin-bottom: 12px; }
  .evp-f-col a { font-size: 13px; color: rgba(250,247,242,0.4); transition: color 0.3s; }
  .evp-f-col a:hover { color: var(--gold); }
  .evp-f-divider { height: 1px; background: linear-gradient(90deg, transparent, rgba(198,167,105,0.2), transparent); margin-bottom: 32px; }
  .evp-f-bottom { display: flex; justify-content: space-between; font-size: 11px; color: rgba(250,247,242,0.25); }
  .evp-f-bottom a { color: rgba(250,247,242,0.25); margin-left: 20px; transition: color 0.3s; }
  .evp-f-bottom a:hover { color: var(--gold); }

  /* ═══ DIALOGS (headlessui portals to body, styles still apply globally) ═══ */
  .evp-overlay { position: fixed; inset: 0; z-index: 1000; }
  .evp-overlay-bg { position: fixed; inset: 0; background: rgba(7,79,79,0.55); backdrop-filter: blur(3px); }
  .evp-dialog-scroll { position: fixed; inset: 0; z-index: 1001; display: flex; align-items: center; justify-content: center; padding: 24px; overflow-y: auto; }
  .evp-dialog-box {
    position: relative; width: 100%; max-width: 560px; background: var(--cream);
    padding: 44px 40px 36px; max-height: 90vh; overflow-y: auto;
    box-shadow: 0 30px 80px rgba(0,0,0,0.25);
  }
  .evp-dialog-box::before { content: ''; position: absolute; top: 0; left: 0; right: 0; height: 3px; background: var(--gold); }
  .evp-dialog-box h3 {
    font-family: var(--serif); font-size: 26px; font-weight: 400; color: var(--teal); margin-bottom: 28px;
  }
  .evp-close-x {
    position: absolute; top: 18px; right: 18px; width: 32px; height: 32px; border: none;
    background: none; cursor: pointer; color: var(--text-l); font-size: 16px;
    display: flex; align-items: center; justify-content: center; transition: color 0.3s;
  }
  .evp-close-x:hover { color: var(--teal); }
  .evp-field { margin-bottom: 18px; }
  .evp-field label {
    display: block; font-size: 9px; font-weight: 600; letter-spacing: 2px; text-transform: uppercase;
    color: var(--teal); margin-bottom: 9px;
  }
  .evp-field input[type="text"], .evp-field textarea, .evp-dt-input {
    -webkit-appearance: none; appearance: none;
    width: 100%; border: 1px solid transparent; border-radius: 0; background: var(--white);
    padding: 0 16px; height: 48px; font-family: var(--sans); font-size: 14px; color: var(--text);
    transition: all 0.35s var(--ease); box-shadow: inset 0 0 0 1px rgba(7,79,79,0.1);
  }
  .evp-field input:focus, .evp-field textarea:focus, .evp-dt-input:focus {
    outline: none; border-color: var(--gold); box-shadow: 0 0 0 3px rgba(198,167,105,0.12);
  }
  .evp-field textarea {
    height: auto; padding: 14px 16px; resize: vertical; line-height: 1.7; min-height: 120px;
  }
  .evp-field .react-datepicker-wrapper { display: block; }

  /* ═══ CUSTOM DATE/TIME PICKER THEME ═══ */
  .evp-dt-popper { z-index: 1010 !important; }
  .react-datepicker {
    font-family: var(--sans) !important; border: none !important; border-radius: 0 !important;
    box-shadow: 0 20px 50px rgba(7,79,79,0.22) !important; overflow: hidden;
  }
  .react-datepicker__triangle { display: none !important; }
  .react-datepicker__header {
    background: var(--teal) !important; border-bottom: 1px solid rgba(198,167,105,0.3) !important;
    border-radius: 0 !important; padding-top: 14px !important; padding-bottom: 12px !important;
  }
  .react-datepicker__header--time { padding: 14px 0 12px !important; }
  .react-datepicker__current-month, .react-datepicker-time__header {
    color: var(--cream) !important; font-family: var(--serif) !important; font-size: 15px !important;
    font-weight: 400 !important; line-height: 20px !important; margin: 0 !important;
  }
  .react-datepicker__day-name { color: rgba(250,247,242,0.55) !important; font-size: 11px !important; }
  .react-datepicker__navigation { top: 14px !important; height: 20px !important; width: 20px !important; }
  .react-datepicker__navigation--previous { left: 16px !important; }
  .react-datepicker__navigation--next { right: 116px !important; }
  .react-datepicker__navigation--next--with-time:not(.react-datepicker__navigation--next--with-today-button) { right: 116px !important; }
  .react-datepicker__navigation-icon { top: 0 !important; }
  .react-datepicker__navigation-icon::before {
    border-color: rgba(250,247,242,0.7) !important; border-width: 2px 2px 0 0 !important;
    width: 7px !important; height: 7px !important; transition: border-color 0.2s !important;
  }
  .react-datepicker__navigation:hover .react-datepicker__navigation-icon::before { border-color: var(--gold) !important; }
  .react-datepicker__navigation:disabled .react-datepicker__navigation-icon::before { border-color: rgba(250,247,242,0.25) !important; }
  .react-datepicker__month { margin: 10px !important; }
  .react-datepicker__day {
    color: var(--text) !important; font-size: 12px !important; border-radius: 0 !important;
    transition: all 0.2s !important;
  }
  .react-datepicker__day:hover { background: var(--cream-d) !important; }
  .react-datepicker__day--selected, .react-datepicker__day--keyboard-selected {
    background: var(--teal) !important; color: var(--cream) !important;
  }
  .react-datepicker__day--today { font-weight: 700 !important; color: var(--gold) !important; }
  .react-datepicker__day--disabled { color: var(--text-ll) !important; }
  .react-datepicker__day--outside-month { color: var(--text-ll) !important; }
  .react-datepicker__time-container {
    border-left: 1px solid rgba(7,79,79,0.1) !important; width: 100px !important;
  }
  .react-datepicker__time-container .react-datepicker__time { background: var(--white) !important; }
  .react-datepicker__time-box { width: 100% !important; }
  .react-datepicker__time-list-item {
    font-size: 12px !important; color: var(--text) !important; height: auto !important; padding: 8px 10px !important;
  }
  .react-datepicker__time-list-item:hover { background: var(--cream-d) !important; }
  .react-datepicker__time-list-item--selected {
    background: var(--teal) !important; color: var(--cream) !important; font-weight: 400 !important;
  }
  .evp-upload {
    display: flex; align-items: center; gap: 16px; border: 1px dashed rgba(7,79,79,0.25);
    background: var(--white); padding: 14px; cursor: pointer; position: relative; transition: border-color 0.3s;
  }
  .evp-upload:hover { border-color: var(--gold); }
  .evp-upload input[type="file"] { position: absolute; inset: 0; opacity: 0; cursor: pointer; }
  .evp-upload-thumb {
    width: 56px; height: 56px; background: var(--cream-d); flex-shrink: 0; overflow: hidden;
    display: flex; align-items: center; justify-content: center; color: var(--gold);
  }
  .evp-upload-thumb img { width: 100%; height: 100%; object-fit: cover; }
  .evp-upload-text { font-size: 12px; color: var(--text-l); }
  .evp-msg { font-size: 12px; margin-bottom: 16px; padding: 12px 16px; border-left: 2px solid; }
  .evp-msg.error { background: rgba(179,51,51,0.06); color: #b33; border-color: #b33; }
  .evp-msg.success { background: rgba(7,79,79,0.06); color: var(--teal); border-color: var(--teal); }
  .evp-dialog-actions { display: flex; gap: 14px; margin-top: 30px; }
  .evp-btn-primary {
    flex: 1; height: 50px; background: var(--teal); color: var(--cream); border: 1px solid var(--teal);
    font-size: 11px; font-weight: 500; letter-spacing: 2px; text-transform: uppercase;
    cursor: pointer; transition: color 0.35s; position: relative; overflow: hidden;
  }
  .evp-btn-primary::before {
    content: ''; position: absolute; inset: 0; background: var(--gold);
    transform: translateY(101%); transition: transform 0.45s var(--ease);
  }
  .evp-btn-primary:hover::before { transform: translateY(0); }
  .evp-btn-primary span { position: relative; z-index: 1; }
  .evp-btn-primary:hover { color: var(--teal); }
  .evp-btn-secondary {
    height: 50px; padding: 0 26px; background: transparent; color: var(--text-l); border: 1px solid rgba(7,79,79,0.18);
    font-size: 11px; font-weight: 500; letter-spacing: 2px; text-transform: uppercase;
    cursor: pointer; transition: all 0.3s;
  }
  .evp-btn-secondary:hover { border-color: var(--teal); color: var(--teal); }

  @media (max-width: 1024px) {
    .evp-nav-links, .evp-nav-auth { display: none; }
    .evp-nav-toggle { display: flex; }
    .evp-nav-mobile { display: flex; }
    .evp-hero { padding: 150px 24px 70px; }
    .evp-section { padding: 0 24px 70px; }
    .evp-f-grid { grid-template-columns: 1fr 1fr; gap: 40px 24px; }
    .evp-f-brand { grid-column: 1 / -1; }
  }
  @media (max-width: 640px) {
    .evp-nav-inner { padding: 20px 20px; }
    .evp-hero { padding: 130px 18px 56px; }
    .evp-grid { grid-template-columns: 1fr; }
    .evp-f-grid { grid-template-columns: 1fr; gap: 32px; }
    .evp-f-bottom { flex-direction: column; gap: 10px; text-align: center; }
    .evp-f-bottom a { margin-left: 10px; }
    .evp-dialog-box { padding: 36px 22px 28px; }
  }
`;

const MONTHS = ["IAN", "FEB", "MAR", "APR", "MAI", "IUN", "IUL", "AUG", "SEP", "OCT", "NOV", "DEC"];

function formatChip(dateStr) {
  if (!dateStr) return { d: "--", m: "" };
  const d = new Date(dateStr + "T00:00:00");
  if (isNaN(d)) return { d: "--", m: "" };
  return { d: String(d.getDate()).padStart(2, "0"), m: MONTHS[d.getMonth()] };
}

function formatLong(dateStr, time) {
  if (!dateStr) return "";
  const d = new Date(dateStr + "T00:00:00");
  if (isNaN(d)) return dateStr;
  const day = d.getDate();
  const month = ["ianuarie","februarie","martie","aprilie","mai","iunie","iulie","august","septembrie","octombrie","noiembrie","decembrie"][d.getMonth()];
  return `${day} ${month} ${d.getFullYear()}` + (time ? ` · ${time}` : "");
}

const EventsAdmin = () => {
  const user = useUserData();
  const { events, loading, fetchEvents } = useEvents();
  const [expanded, setExpanded] = useState({});
  const [pastOpen, setPastOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [authed, setAuthed] = useState(false);
  const [preDone, setPreDone] = useState(false);
  const userWrapRef = useRef(null);

  const token = typeof window !== "undefined" ? localStorage.getItem("access_token") : null;

  useEffect(() => {
    setAuthed(!!token);
  }, [token]);

  useEffect(() => {
    const t = setTimeout(() => setPreDone(true), 1200);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    function handleOutside(e) {
      if (userWrapRef.current && !userWrapRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
    }
    document.addEventListener("click", handleOutside);
    return () => document.removeEventListener("click", handleOutside);
  }, []);

  let displayFirst = localStorage.getItem("user_first_name") || "";
  let displayLast = localStorage.getItem("user_last_name") || "";
  let displayEmail = user?.email || "";
  if (token && !displayEmail) {
    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      displayEmail = payload.email || "";
    } catch (e) {}
  }
  if (!displayFirst && displayEmail) displayFirst = displayEmail.split("@")[0];
  const initials = ((displayFirst ? displayFirst.charAt(0) : "") + (displayLast ? displayLast.charAt(0) : "")).toUpperCase() || "?";

  const handleLogout = () => {
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("user_first_name");
    localStorage.removeItem("user_last_name");
    window.location.reload();
  };

  const now = new Date();

  const upcomingEvents = [...events]
    .filter((event) => new Date(`${event.date}T${event.time || "00:00"}`) >= now)
    .sort((a, b) => new Date(`${a.date}T${a.time || "00:00"}`) - new Date(`${b.date}T${b.time || "00:00"}`));

  const pastEvents = [...events]
    .filter((event) => new Date(`${event.date}T${event.time || "00:00"}`) < now)
    .sort((a, b) => new Date(`${b.date}T${b.time || "00:00"}`) - new Date(`${a.date}T${a.time || "00:00"}`));

  const newestId = events.length
    ? events.reduce((max, e) => (e.id > max ? e.id : max), events[0].id)
    : null;

  const toggleExpand = (id) => setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

  return (
    <div className="evp-root">
      <style>{css}</style>
      <div className={`evp-pre${preDone ? " done" : ""}`}>
        <div className="evp-pre-word">Aware</div>
        <div className="evp-pre-line" />
      </div>
      <div className="evp-grain" />

      {/* NAV */}
      <nav className="evp-nav">
        <div className="evp-nav-inner">
          <a href="/" className="evp-nav-logo">Aware</a>
          <ul className="evp-nav-links">
            <li><a href="/">Acasă</a></li>
            <li><a href="/despre-noi">Despre noi</a></li>
            <li><a href="/servicii">Servicii</a></li>
            <li><a href="/evenimente" className="active">Evenimente</a></li>
          </ul>
          {!authed && (
            <div className="evp-nav-auth">
              <a href="/login" className="evp-nav-login">Autentificare</a>
              <a href="/register" className="evp-nav-register"><span>Înregistrare</span></a>
            </div>
          )}
          {authed && (
            <div className={`evp-nav-user-wrap${userMenuOpen ? " open" : ""}`} ref={userWrapRef}>
              <div className="evp-nav-user" onClick={() => setUserMenuOpen((o) => !o)}>
                <span className="evp-nav-user-name">{displayFirst}</span>
                <div className="evp-nav-user-avatar">{initials}</div>
                <svg className="evp-nav-user-chevron" viewBox="0 0 10 6" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M1 1l4 4 4-4"/></svg>
              </div>
              <div className="evp-nav-dropdown">
                <div className="evp-nav-dd-header">
                  <div className="evp-nav-dd-name">{displayFirst}{displayLast ? ` ${displayLast}` : ""}</div>
                  <div className="evp-nav-dd-email">{displayEmail}</div>
                </div>
                <div className="evp-nav-dd-items">
                  <button className="evp-nav-dd-item" onClick={() => (window.location.href = "/profil")}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                    Profilul meu
                  </button>
                  <div className="evp-nav-dd-divider" />
                  <button className="evp-nav-dd-item logout" onClick={handleLogout}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
                    Deconectare
                  </button>
                </div>
              </div>
            </div>
          )}
          <button
            className={`evp-nav-toggle${mobileOpen ? " open" : ""}`}
            aria-label="Meniu"
            aria-expanded={mobileOpen}
            onClick={() => setMobileOpen((o) => !o)}
          >
            <span></span><span></span><span></span>
          </button>
        </div>
        <div className={`evp-nav-mobile${mobileOpen ? " open" : ""}`}>
          <ul className="evp-nav-mobile-links">
            <li><a href="/" onClick={() => setMobileOpen(false)}>Acasă</a></li>
            <li><a href="/despre-noi" onClick={() => setMobileOpen(false)}>Despre noi</a></li>
            <li><a href="/servicii" onClick={() => setMobileOpen(false)}>Servicii</a></li>
            <li><a href="/evenimente" onClick={() => setMobileOpen(false)}>Evenimente</a></li>
          </ul>
          {!authed && (
            <div className="evp-nav-mobile-auth">
              <a href="/login" className="evp-nav-login">Autentificare</a>
              <a href="/register" className="evp-nav-register"><span>Înregistrare</span></a>
            </div>
          )}
        </div>
      </nav>
      <div className={`evp-nav-mobile-scrim${mobileOpen ? " open" : ""}`} onClick={() => setMobileOpen(false)} />

      {/* HERO */}
      <section className="evp-hero">
        <div className="evp-label">Evenimente</div>
        <h1>Momente trăite <em>împreună</em></h1>
        <p >Ateliere, întâlniri de grup și sesiuni deschise publicului — tot ce organizăm la Aware, într-un singur loc.</p>
      </section>

      <div className="evp-strip">
        <a href="tel:+40723071922" className="primary">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.362 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.338 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
          <span>Sună-ne: +40 723 071 922</span>
        </a>
        <a href="https://wa.me/40723071922" target="_blank" rel="noopener noreferrer" className="secondary">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2.05 22l5.25-1.38a9.9 9.9 0 0 0 4.74 1.21h.005c5.46 0 9.91-4.45 9.91-9.91C21.98 6.45 17.5 2 12.04 2zm0 18.15h-.005a8.2 8.2 0 0 1-4.19-1.15l-.3-.18-3.12.82.83-3.04-.2-.31a8.18 8.18 0 0 1-1.26-4.38c0-4.54 3.7-8.24 8.25-8.24 2.2 0 4.27.86 5.83 2.42a8.18 8.18 0 0 1 2.41 5.83c0 4.55-3.7 8.23-8.25 8.23z"/></svg>
          <span>Scrie-ne pe WhatsApp</span>
        </a>
      </div>

      {/* UPCOMING */}
      <section className="evp-section">
        <div className="evp-sec-head">
          <h2>Evenimente <em>viitoare</em></h2>
          {user?.is_staff && <AddEvent onSuccess={fetchEvents} />}
        </div>

        {loading ? (
          <p style={{ color: "var(--text-l)", fontSize: 14 }}>Se încarcă evenimentele...</p>
        ) : upcomingEvents.length === 0 ? (
          <div className="evp-empty">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4"><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/></svg>
            <p>Momentan nu avem evenimente programate</p>
            <span>Revino curând — pregătim ceva nou.</span>
          </div>
        ) : (
          <div className="evp-grid">
            {upcomingEvents.map((event) => {
              const chip = formatChip(event.date);
              const isNew = event.id === newestId;
              const isOpen = !!expanded[event.id];
              return (
                <div className="evp-card" key={event.id}>
                  <div className="evp-card-media">
                    {isNew && (
                      <div className="evp-new-badge">
                        <svg width="9" height="9" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l2.4 7.2H22l-6 4.6 2.3 7.2-6.3-4.5-6.3 4.5 2.3-7.2-6-4.6h7.6z"/></svg>
                        Nou
                      </div>
                    )}
                    <div className="evp-date-chip">
                      <div className="d">{chip.d}</div>
                      <div className="m">{chip.m}</div>
                    </div>
                    <img
                      src={`https://awaretherapycenter.ro/${event.image}`}
                      alt={event.title}
                      onError={(e) => { e.target.onerror = null; e.target.src = "/fallback-image.png"; }}
                    />
                    {user?.is_staff && (
                      <div className="evp-card-admin">
                        <EditEvent event={event} onSuccess={fetchEvents} />
                        <DeleteEvent eventId={event.id} onSuccess={fetchEvents} />
                      </div>
                    )}
                  </div>
                  <div className="evp-card-body">
                    <h3 className="evp-card-title">{event.title}</h3>
                    <div className="evp-card-meta">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 3"/></svg>
                      {formatLong(event.date, event.time)}
                    </div>
                    <p className={`evp-card-desc${isOpen ? " open" : ""}`}>{event.description}</p>
                    {event.description && event.description.length > 140 && (
                      <button className="evp-card-more" onClick={() => toggleExpand(event.id)}>
                        {isOpen ? "Vezi mai puțin" : "Citește mai mult"}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* PAST */}
      {pastEvents.length > 0 && (
        <section className="evp-section" style={{ paddingTop: 0, marginTop: -40 }}>
          <button className={`evp-past-toggle${pastOpen ? " open" : ""}`} onClick={() => setPastOpen((p) => !p)}>
            {pastOpen ? "Ascunde evenimentele trecute" : `Vezi evenimentele trecute (${pastEvents.length})`}
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 9l6 6 6-6"/></svg>
          </button>
          <div className={`evp-past-wrap${pastOpen ? " open" : ""}`}>
            <div className="evp-past-grid">
              {pastEvents.map((event) => (
                <div className="evp-past-card" key={event.id}>
                  <div className="evp-past-media">
                    <img
                      src={`https://awaretherapycenter.ro/${event.image}`}
                      alt={event.title}
                      onError={(e) => { e.target.onerror = null; e.target.src = "/fallback-image.png"; }}
                    />
                  </div>
                  <div className="evp-past-body">
                    <h4>{event.title}</h4>
                    <div className="evp-card-meta">{formatLong(event.date, event.time)}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FOOTER */}
      <footer className="evp-footer">
        <div className="evp-f-inner">
          <div className="evp-f-grid">
            <div className="evp-f-brand">
              <div className="evp-f-brand-name">Aware</div>
              <p>Conștientizarea este începutul schimbării. Servicii psihologice bazate pe profesionalism, responsabilitate și onestitate.</p>
              <div className="evp-f-social">
                <a href="https://www.instagram.com/_aware.psychotherapy_?igsi=MWl3eXg5aTl0ZjdsZg==" target="_blank" rel="noopener noreferrer">Ig</a>
                <a href="https://www.facebook.com/share/1Bt6W3XQ41/?mibextid=wwXIfr" target="_blank" rel="noopener noreferrer">Fb</a>
              </div>
            </div>
            <div className="evp-f-col">
              <h5>Servicii</h5>
              <ul>
                <li><a href="/servicii">Evaluare Psihologică Clinică</a></li>
                <li><a href="/servicii">Psihoterapie Individuală</a></li>
                <li><a href="/servicii">Psihoterapie de Cuplu și Familie</a></li>
                <li><a href="/servicii">Consiliere Psihologică</a></li>
                <li><a href="/servicii">Consiliere pentru Serviciul de Probațiune</a></li>
                <li><a href="/servicii">Evaluări pentru Apărare, Ordine Publică și Securitate Națională</a></li>
                <li><a href="/servicii">Supervizare în Psihologie Clinică</a></li>
                <li><a href="/servicii">Practică de Specialitate</a></li>
              </ul>
            </div>
            <div className="evp-f-col">
              <h5>Specialiști</h5>
              <ul>
                <li><a href="/alexandra">Alexandra Strîmbeanu</a></li>
                <li><a href="/alina">Alina Gheorghe</a></li>
                <li><a href="/despre-noi">Florena Antonescu</a></li>
                <li><a href="/manuela">Manuela Capalb</a></li>
              </ul>
            </div>
            <div className="evp-f-col">
              <h5>Aware</h5>
              <ul>
                <li><a href="/">Acasă</a></li>
                <li><a href="/despre-noi">Despre noi</a></li>
                <li><a href="/#contact">Contact</a></li>
                <li><a href="/evenimente">Evenimente</a></li>
              </ul>
            </div>
          </div>
          <div className="evp-f-divider" />
          <div className="evp-f-bottom">
            <span>© 2025 Aware — Centru de Psihoterapie</span>
            <div>
              <a href="/conditii">Termeni</a>
              <a href="/confidentialitate">Confidențialitate</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default EventsAdmin;
